import { lazy, Suspense, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";

// Layout Components
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/shared/WhatsAppButton";

// Pages
import HomePage from "@/pages/HomePage";
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("@/pages/services/ServiceDetailPage"));
const OtherServiceDetailPage = lazy(() => import("@/pages/services/OtherServiceDetailPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));

// About Pages
const CompanyProfilePage = lazy(() => import("@/pages/about/CompanyProfilePage"));
const OrganizationPage = lazy(() => import("@/pages/about/OrganizationPage"));
const CareersPage = lazy(() => import("@/pages/about/CareersPage"));

// Projects Pages
const ClientsPage = lazy(() => import("@/pages/projects/ClientsPage"));
const CompletedProjectsPage = lazy(() => import("@/pages/projects/CompletedProjectsPage"));
const GalleryPage = lazy(() => import("@/pages/projects/GalleryPage"));

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Lightweight CSS scroll progress bar — no framer-motion
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);
      rafRef.current = null;
    };

    const onScroll = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      className="fixed top-0 left-0 h-[3px] z-[100] transition-none"
      style={{
        width: `${progress}%`,
        background: "linear-gradient(90deg, #D4AF37, #F3E5AB, #C9A032)",
        boxShadow: "0 0 8px rgba(212,175,55,0.5)",
      }}
    />
  );
};

const NotFoundPage = () => (
  <main className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 py-24">
    <p className="text-[#D4AF37] font-bold tracking-widest mb-4">404</p>
    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Page not found</h1>
    <p className="text-slate-600 mb-8">The page you are looking for doesn't exist or has moved.</p>
    <Link to="/" className="btn-modern btn-gold">Back to Home</Link>
  </main>
);

// Page wrapper — adds enter animation on route change
const PageWrapper = ({ children }) => {
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[1000] focus:bg-white focus:text-slate-900 focus:px-4 focus:py-2 focus:rounded">
        Skip to content
      </a>
      <Header />
      {/* Pages render their own <main>; this wrapper must not nest another */}
      <div id="content" key={pathname} className="page-enter flex-grow">
        <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>{children}</Suspense>
      </div>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ScrollProgress />
        <PageWrapper>
          <Routes>
            {/* Home */}
            <Route path="/" element={<HomePage />} />

            {/* About */}
            <Route path="/about" element={<CompanyProfilePage />} />
            <Route path="/about/company-profile" element={<CompanyProfilePage />} />
            <Route path="/about/organization" element={<OrganizationPage />} />
            <Route path="/about/careers" element={<CareersPage />} />

            {/* Services */}
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/other-services/:slug" element={<OtherServiceDetailPage />} />

            {/* Projects */}
            <Route path="/projects" element={<GalleryPage />} />
            <Route path="/projects/clients" element={<ClientsPage />} />
            <Route path="/projects/completed" element={<CompletedProjectsPage />} />
            <Route path="/projects/gallery" element={<GalleryPage />} />

            {/* Contact */}
            <Route path="/contact" element={<ContactPage />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </PageWrapper>
      </BrowserRouter>
    </div>
  );
}

export default App;
